import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../lib/firebase";
import { HOME_HUNT_PLACEMENTS } from "../lib/homeHuntPlacements";

type HuntState = {
  playDate?: string;
  status?: "scheduled" | "hiding" | "found" | "expired";
  placementId?: string;
  winnerName?: string;
};

const claimHomeHunt = httpsCallable<{ householdId: string }, { won: boolean; winnerName?: string }>(functions, "claimHomeHunt");

export function HomeHunt({ householdId, userUid, activeView }: { householdId?: string; userUid?: string; activeView?: string }) {
  const [hunt, setHunt] = useState<HuntState | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [message, setMessage] = useState("");
  const [target, setTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setMessage("");
    if (!householdId || !userUid) { setHunt(null); return; }
    return onSnapshot(doc(db, "households", householdId, "gameState", "homeHunt"),
      (snapshot) => setHunt(snapshot.exists() ? snapshot.data() as HuntState : null),
      () => setHunt(null));
  }, [householdId, userUid]);

  const placement = hunt?.placementId ? HOME_HUNT_PLACEMENTS[hunt.placementId] : undefined;

  useEffect(() => {
    if (hunt?.status !== "hiding" || !placement) {
      setTarget(null);
      return;
    }
    const selector = `[data-home-hunt-view="${placement.viewKey}"]`;
    const locate = () => setTarget(document.querySelector<HTMLElement>(selector));
    locate();
    const observer = new MutationObserver(locate);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-home-hunt-view"] });
    return () => observer.disconnect();
  }, [hunt?.status, placement, activeView]);

  async function claim() {
    if (claiming) return;
    setClaiming(true);
    try {
      const result = (await claimHomeHunt({ householdId: householdId! })).data;
      setMessage(result.won ? "You found the house! +1 Hunt point 🏆" : `${result.winnerName || "Someone"} found it first!`);
      window.setTimeout(() => setMessage(""), 6000);
    } catch {
      setMessage("The house slipped away. Try again!");
      window.setTimeout(() => setMessage(""), 4000);
    } finally { setClaiming(false); }
  }

  const house = householdId && userUid && hunt?.status === "hiding" && target && placement
    ? createPortal(
      <button
        type="button"
        className="home-hunt-target"
        style={{ top: placement.top, left: placement.left }}
        onClick={claim}
        disabled={claiming}
        aria-label="Found the hidden house"
      >
        <span aria-hidden="true">🏠</span><span className="home-hunt-sparkle" aria-hidden="true">✦</span>
      </button>,
      target
    )
    : null;

  return <>
    {house}
    {message && <div className="home-hunt-toast" role="status">{message}</div>}
  </>;
}
